import { Persistence } from "../../config/constants";
import { BaseEntity } from "../../core/entity/BaseEntity";
import { Entity, GameEventMap } from "../../core/entity/Entity";
import { on } from "../../core/entity/handler";
import { Player } from "../../sim/entities/Player";
import { Clock } from "../../sim/singletons/Clock";
import { StoryTracker } from "../../sim/singletons/StoryTracker";
import { TutorialTracker } from "../../sim/singletons/TutorialTracker";
import { Wallet } from "../../sim/singletons/Wallet";
import { BenchDive } from "../scenes/bench/BenchDive";
import {
  EMPTY_LEDGER,
  ledgerOf,
  MilestoneLedger,
  newMilestones,
} from "./milestones";
import { furtherOf, positionOf, sceneKey, START, stepKey } from "./spine";
import { beginView, count, telemetryClient, track } from "./telemetry";

/**
 * The listener that turns the world's progress into telemetry. It
 * follows the event rules at the top of CustomEvent.ts to the letter:
 * it listens to `storyChanged` and `progressionChanged`, reads the two
 * ledgers, and dispatches nothing.
 *
 * A milestone fires once per playthrough: the recorder keeps the ledger
 * it last saw and reports only what's new, each with the shop's day and
 * balance at the moment. A loaded save (`worldLoaded` — Continue, or a
 * fixture) resets the baseline to the world as loaded, so a returning
 * player's old milestones aren't reported again as if reached today; a
 * new game starts from an empty ledger and reports everything from the
 * opener on. Each report also moves the client's spine position
 * (spine.ts), so everything recorded after is stamped with it.
 *
 * Where the player is in the world is read once a frame and reported
 * as views: a scavenging trip, a store, the bench. One probe of the
 * player's `away` and the bench dive's open key, compared with last
 * frame's — the places in the DOM (the manual, the journal, the pause
 * menu) report themselves from the chrome instead.
 *
 * The engine's pause stops the play clock here too, and Escape is
 * counted off the engine's key event, so no dispatcher has to know
 * telemetry exists.
 *
 * Permanent and unpausable: it outlives shop boots and keeps counting
 * under the pause menu, whose opening is one of the things recorded.
 */
export class TelemetryRecorder extends BaseEntity implements Entity {
  id = "telemetryRecorder";
  persistenceLevel: number = Persistence.Permanent;
  pausable = false;

  private seen: MilestoneLedger = EMPTY_LEDGER;

  /** The world place being viewed, and the closer that ends it. */
  private place: { id: string; close: () => void } | null = null;

  @on("worldLoaded")
  onWorldLoaded() {
    this.seen = this.current() ?? EMPTY_LEDGER;
    telemetryClient()?.moveTo(positionOf(this.seen));
  }

  @on("storyChanged")
  onStoryChanged() {
    this.report();
  }

  @on("progressionChanged")
  onProgressionChanged() {
    this.report();
  }

  @on("pause")
  onPause() {
    telemetryClient()?.setPaused(true);
  }

  @on("unpause")
  onUnpause() {
    telemetryClient()?.setPaused(false);
  }

  @on("keyDown")
  onKeyDown({ key }: GameEventMap["keyDown"]) {
    if (key === "Escape") count("escape");
  }

  @on("slowTick")
  onSlowTick() {
    const here = this.placeNow();
    if (here === this.place?.id) return;
    this.place?.close();
    this.place = here === null ? null : { id: here, close: beginView(here) };
  }

  /** The world place the player is in right now, or null on the floor. */
  private placeNow(): string | null {
    const away = this.game.entities.tryGetSingleton(Player)?.away;
    if (away?.kind === "scavenging") return "scavenge";
    if (away?.kind === "shopping") {
      return away.store === "lumberyard" ? "lumberyard" : "store";
    }
    const bench = this.game.entities.tryGetSingleton(BenchDive);
    if (bench?.openBenchKey != null) return "bench";
    return null;
  }

  private current(): MilestoneLedger | null {
    const story = this.game.entities.tryGetSingleton(StoryTracker);
    const tutorials = this.game.entities.tryGetSingleton(TutorialTracker);
    if (!story || !tutorials) return null;
    return ledgerOf(story.progress, tutorials.tutorials);
  }

  private report(): void {
    const now = this.current();
    if (!now) return;
    const day = this.game.entities.tryGetSingleton(Clock)?.day;
    const money = this.game.entities.tryGetSingleton(Wallet)?.money;
    const shop = {
      ...(day !== undefined ? { day } : {}),
      ...(money !== undefined ? { money: Math.round(money) } : {}),
    };
    const client = telemetryClient();
    for (const milestone of newMilestones(this.seen, now)) {
      const key =
        milestone.kind === "scene"
          ? sceneKey(milestone.id)
          : stepKey(milestone.id);
      if (milestone.kind === "scene") {
        track("scene", { id: milestone.id, ...shop });
      } else {
        track("step", { track: milestone.track, id: milestone.id, ...shop });
      }
      client?.moveTo(furtherOf(client.at ?? START, key));
    }
    this.seen = now;
  }
}
